import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';

import { getTabValue, getTabs } from './StaffingUtils';

import Tabs from '@/components/Tabs';
import { ApiEndpoints, V3Endpoints } from '@/constants/urls';
import { Skill } from '@/types/graphql';
import useAnalytics from '@/util/analytics';
import axiosClient, { axiosRubyClient } from '@/util/axios/axiosClient';
import { WorkersCount } from '@/util/axios/types';
import useAuth from '@/hooks/useAuth';
import useDebouncedValue from '@/hooks/useDebouncedValue';
import { FEATURE_TOGGLE } from '@/constants/featuretoggle';
import { useFeatureValue } from '@growthbook/growthbook-react';

type StaffingTabsProps = {
  tab: number;
  setTab: (tab: number) => void;
  skill: Partial<Skill>;
  isLTA: boolean;
  custId: number | string;
  addressId: number | string;
  jobTaxType: string;
  badgeIds: string | null;
  backgroundCheckRequired?: boolean;
  jobId?: number | string | null;
};

const StaffingTabs = ({
  tab,
  setTab,
  skill,
  isLTA,
  custId,
  addressId,
  jobTaxType,
  badgeIds = null,
  backgroundCheckRequired,
  jobId = null,
}: StaffingTabsProps) => {
  const [counts, setCounts] = useState<WorkersCount>();
  const { currentAdminIsCustomerAdmin: isClientAdmin } = useAuth();

  const TABS = useMemo(
    () => getTabs({ counts, isTrained: !!skill?.gravyTrained, isClientAdmin }),
    [counts, skill],
  );

  const jobFulfillment = useFeatureValue(FEATURE_TOGGLE.JobFulfillment, false);

  const { logEvent } = useAnalytics();

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        skillId: skill?.id,
        custId,
        addressId,
        jobId,
        isLTA,
        jobTaxType,
        badgeIds,
        backgroundCheckRequired,
        jobFulfillment,
      }),
    [
      skill?.id,
      custId,
      addressId,
      jobId,
      isLTA,
      jobTaxType,
      badgeIds,
      backgroundCheckRequired,
      jobFulfillment,
    ],
  );
  const debouncedFilterKey = useDebouncedValue(filterKey, 500);

  useEffect(() => {
    const controller = new AbortController();

    const fetchWorkersCounts = async () => {
      try {
        const res = jobFulfillment
          ? await axiosRubyClient.get(ApiEndpoints.GET_WORKERS_COUNT, {
              params: {
                skillId: Number(skill?.id),
                customerId: Number(custId),
                addressId: Number(addressId),
                jobId: jobId,
                workerType: isLTA ? 'lta' : 'gig',
                taxType: jobTaxType || 'all',
                badgeIds: badgeIds,
                backgroundCheckRequired: backgroundCheckRequired,
              },
              signal: controller.signal,
            })
          : await axiosClient.post(
              V3Endpoints.GET_WORKERS_COUNT,
              {
                skillId: Number(skill?.id),
                customerId: Number(custId),
                addressId: Number(addressId),
                workerType: isLTA ? 'lta' : 'gig',
                taxType: jobTaxType || 'all',
                badgeIds: badgeIds,
                backgroundCheckRequired: backgroundCheckRequired,
              },
              { signal: controller.signal },
            );

        if (res.data?.data) {
          setCounts((prevCounts) => {
            const newData = res.data.data;
            if (JSON.stringify(prevCounts) === JSON.stringify(newData)) {
              return prevCounts;
            }
            return newData;
          });
        }
      } catch (e) {
        if (!axios.isCancel(e)) {
          console.error(e);
        }
      }
    };

    fetchWorkersCounts();

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFilterKey]);

  return (
    <div
      className="hide-scroll"
      style={{ width: '100%', overflow: 'auto hidden' }}
    >
      <Tabs
        fit
        minWidth="216px"
        selected={tab}
        showTabOnMobile={true}
        tabs={TABS}
        onSelect={(tab: number) => {
          logEvent(getTabValue(tab, !!skill.gravyTrained));
          setTab(tab);
        }}
      />
    </div>
  );
};

export default StaffingTabs;
